import { useCallback, useState } from 'react';

interface VisualEditorParams {
  sections: PolicyFormSection[];
  questions: Record<string, PolicyFormQuestion>;
  onFormDefinitionChange: (newDefinition: PolicyFormDefinition) => void;
  currentFormDefinition: PolicyFormDefinition;
}

interface VisualEditorResult {
  handleSectionChange: (sectionId: string, updates: Partial<PolicyFormSection>) => void;
  handleSubsectionChange: (
    sectionId: string,
    subsectionId: string,
    updates: Partial<PolicyFormSubSection>
  ) => void;
  handleQuestionGroupChange: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    updates: Partial<PolicyFormQuestionGroup>
  ) => void;
  handleQuestionChange: (questionId: string, updates: Partial<PolicyFormQuestion>) => void;
  handleAddQuestionToGroup: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    newQuestion: PolicyFormQuestion
  ) => void;
  handleAddGroup: (
    sectionId: string,
    subsectionId: string,
    group: PolicyFormQuestionGroup
  ) => void;
  handleAddSubsection: (
    sectionId: string,
    subsection: PolicyFormSubSection
  ) => void;
  handleAddSection: (
    section: PolicyFormSection
  ) => void;
  handleRemoveQuestion: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    questionId: string
  ) => void;
  handleRemoveGroup: (
    sectionId: string,
    subsectionId: string,
    groupId: string
  ) => void;
  handleRemoveSubsection: (
    sectionId: string,
    subsectionId: string
  ) => void;
  handleRemoveSection: (
    sectionId: string
  ) => void;
  confirmDelete: {
    open: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
  };
  setConfirmDelete: React.Dispatch<React.SetStateAction<{
    open: boolean;
    title?: string | undefined;
    message?: string | undefined;
    onConfirm?: (() => void) | undefined;
  }>>;
}

export function useVisualEditor({
  sections,
  questions,
  onFormDefinitionChange,
  currentFormDefinition,
}: VisualEditorParams): VisualEditorResult {

  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    title?: string;
    message?: string;
    onConfirm?: () => void;
  }>({ open: false });

  const handleSectionChange = useCallback((sectionId: string, updates: Partial<PolicyFormSection>) => {
    const updatedSections = sections.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    );

    onFormDefinitionChange({
      sections: updatedSections,
      questions: questions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  }, [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]);

  const handleSubsectionChange = useCallback((
    sectionId: string,
    subsectionId: string,
    updates: Partial<PolicyFormSubSection>
  ) => {
    const updatedSections = sections.map(section => {
      if (section.id !== sectionId) return section;

      const updatedSubsections = section.subsections.map(subsection =>
        subsection.id === subsectionId ? { ...subsection, ...updates } : subsection
      );

      return { ...section, subsections: updatedSubsections };
    });

    onFormDefinitionChange({
      sections: updatedSections,
      questions: questions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  }, [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]);

  const handleQuestionGroupChange = useCallback((
    sectionId: string,
    subsectionId: string,
    groupId: string,
    updates: Partial<PolicyFormQuestionGroup>
  ) => {
    const updatedSections = sections.map(section => {
      if (section.id !== sectionId) return section;

      const updatedSubsections = section.subsections.map(subsection => {
        if (subsection.id !== subsectionId) return subsection;

        const updatedGroups = subsection.questionGroups.map(group =>
          group.id === groupId ? { ...group, ...updates } : group
        );

        return { ...subsection, questionGroups: updatedGroups };
      });

      return { ...section, subsections: updatedSubsections };
    });

    onFormDefinitionChange({
      sections: updatedSections,
      questions: questions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  }, [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]);

  const handleQuestionChange = useCallback((questionId: string, updates: Partial<PolicyFormQuestion>) => {
    const updatedQuestions = {
      ...questions,
      [questionId]: { ...questions[questionId], ...updates },
    };

    onFormDefinitionChange({
      sections: sections,
      questions: updatedQuestions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  }, [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]);

  const handleAddQuestionToGroup = useCallback((
    sectionId: string,
    subsectionId: string,
    groupId: string,
    newQuestion: PolicyFormQuestion
  ) => {
    const updatedQuestions = {
      ...questions,
      [newQuestion.questionRef]: newQuestion,
    };

    const updatedSections = sections.map(section => {
      if (section.id !== sectionId) return section;

      const updatedSubsections = section.subsections.map(subsection => {
        if (subsection.id !== subsectionId) return subsection;

        const updatedGroups = subsection.questionGroups.map(group => {
          if (group.id !== groupId) return group;
          return {
            ...group,
            questions: [...group.questions, newQuestion.questionRef],
          };
        });

        return { ...subsection, questionGroups: updatedGroups };
      });

      return { ...section, subsections: updatedSubsections };
    });

    onFormDefinitionChange({
      sections: updatedSections,
      questions: updatedQuestions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  }, [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]);

  const handleAddGroup = useCallback(
    (sectionId: string, subsectionId: string, group: PolicyFormQuestionGroup) => {
      const updatedSections = sections.map((section) => {
        if (section.id !== sectionId) return section;

        const updatedSubsections = section.subsections.map((sub) => {
          if (sub.id !== subsectionId) return sub;

          return {
            ...sub,
            questionGroups: [...sub.questionGroups, group],
          };
        });

        return { ...section, subsections: updatedSubsections };
      });

      onFormDefinitionChange({
        sections: updatedSections,
        questions,
        requiredParameters: currentFormDefinition.requiredParameters,
      });
    },
    [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]
  );

  const handleAddSubsection = useCallback(
    (sectionId: string, subsection: PolicyFormSubSection) => {
      const updatedSections = sections.map((section) => {
        if (section.id !== sectionId) return section;

        return {
          ...section,
          subsections: [...section.subsections, subsection],
        };
      });

      onFormDefinitionChange({
        sections: updatedSections,
        questions,
        requiredParameters: currentFormDefinition.requiredParameters,
      });
    },
    [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]
  );

  const handleAddSection = useCallback(
    (section: PolicyFormSection) => {
      const updatedSections = [...sections, section];

      onFormDefinitionChange({
        sections: updatedSections,
        questions,
        requiredParameters: currentFormDefinition.requiredParameters,
      });
    },
    [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]
  );

  const handleRemoveQuestion = useCallback((
    sectionId: string,
    subsectionId: string,
    groupId: string,
    questionRef: string
  ) => {
    const updatedSections = sections.map(section => {
      if (section.id !== sectionId) return section;
      return {
        ...section,
        subsections: section.subsections.map(sub => {
          if (sub.id !== subsectionId) return sub;
          return {
            ...sub,
            questionGroups: sub.questionGroups.map(group =>
              group.id === groupId
                ? { ...group, questions: group.questions.filter(q => q !== questionRef) }
                : group
            ),
          };
        }),
      };
    });

    delete questions[questionRef];

    onFormDefinitionChange({
      sections: updatedSections,
      questions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  }, [sections, questions, onFormDefinitionChange, currentFormDefinition.requiredParameters]);

  const handleRemoveGroup = useCallback((
    sectionId: string,
    subsectionId: string,
    groupId: string
  ) => {
    const group = sections
      .find(s => s.id === sectionId)
      ?.subsections.find(sub => sub.id === subsectionId)
      ?.questionGroups.find(g => g.id === groupId);

    if (group && group.questions.length > 0) {
      setConfirmDelete({
        open: true,
        title: "Remove Group?",
        message: "This group contains questions. Removing it will also remove those questions.",
        onConfirm: () => {
          removeGroup(sectionId, subsectionId, groupId);
          setConfirmDelete({ ...confirmDelete, open: false });
        }
      });
      return;
    }

    removeGroup(sectionId, subsectionId, groupId);
  }, [sections]);

  const removeGroup = (sectionId: string, subsectionId: string, groupId: string) => {
    const updatedSections = sections.map(section => {
      if (section.id !== sectionId) return section;
      return {
        ...section,
        subsections: section.subsections.map(sub => {
          if (sub.id !== subsectionId) return sub;
          return {
            ...sub,
            questionGroups: sub.questionGroups.filter(g => g.id !== groupId),
          };
        }),
      };
    });

    onFormDefinitionChange({
      sections: updatedSections,
      questions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  };

  // Remove Subsection
  const handleRemoveSubsection = useCallback((
    sectionId: string,
    subsectionId: string
  ) => {
    const subsection = sections
      .find(s => s.id === sectionId)
      ?.subsections.find(sub => sub.id === subsectionId);

    if (subsection && subsection.questionGroups.length > 0) {
      setConfirmDelete({
        open: true,
        title: "Remove Subsection?",
        message: "This subsection contains groups/questions. Removing it will also remove all of them.",
        onConfirm: () => {
          removeSubsection(sectionId, subsectionId);
          setConfirmDelete({ ...confirmDelete, open: false });
        }
      });
      return;
    }

    removeSubsection(sectionId, subsectionId);
  }, [sections]);

  const removeSubsection = (sectionId: string, subsectionId: string) => {
    const updatedSections = sections.map(section => {
      if (section.id !== sectionId) return section;
      return {
        ...section,
        subsections: section.subsections.filter(sub => sub.id !== subsectionId),
      };
    });

    onFormDefinitionChange({
      sections: updatedSections,
      questions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  };

  // Remove Section
  const handleRemoveSection = useCallback((sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);

    if (section && section.subsections.length > 0) {
      setConfirmDelete({
        open: true,
        title: "Remove Section?",
        message: "This section contains subsections/groups/questions. Removing it will also remove all of them.",
        onConfirm: () => {
          removeSection(sectionId);
          setConfirmDelete({ ...confirmDelete, open: false });
        }
      });
      return;
    }

    removeSection(sectionId);
  }, [sections]);

  const removeSection = (sectionId: string) => {
    const updatedSections = sections.filter(s => s.id !== sectionId);

    onFormDefinitionChange({
      sections: updatedSections,
      questions,
      requiredParameters: currentFormDefinition.requiredParameters,
    });
  };

  return {
    handleSectionChange,
    handleSubsectionChange,
    handleQuestionGroupChange,
    handleQuestionChange,
    handleAddQuestionToGroup,
    handleAddGroup,
    handleAddSubsection,
    handleAddSection,
    handleRemoveQuestion,
    handleRemoveGroup,
    handleRemoveSubsection,
    handleRemoveSection,
    confirmDelete,
    setConfirmDelete
  };
}